/**
 * Agency Team Management API
 *
 * Functions for managing agent assignments and work view filtering within an agency.
 */

import { pb, escapeFilterValue } from '../lib/pocketbase'
import { clientOwnerPatch } from '../shared/clientOwner'
import type {
  TeamMember,
  WorkSummary,
  AssignmentPayload,
} from '../types/agencyTeam'

/**
 * Map a PocketBase user record to TeamMember type
 */
function mapTeamMember(record: Record<string, any>): TeamMember {
  return {
    id: record.id,
    email: record.email ?? '',
    first_name: record.first_name ?? null,
    last_name: record.last_name ?? null,
    full_name: record.first_name && record.last_name
      ? `${record.first_name} ${record.last_name}`
      : record.first_name || record.last_name || record.email || null,
    avatar_url: record.avatar_url ?? record.avatar ?? null,
    role: record.role ?? 'agent',
    agency_id: record.agency_id ?? null,
    lead_agent_id: record.lead_agent_id ?? null,
    is_lead_agent: record.is_lead_agent ?? false,
    permission_overrides: record.permission_overrides ?? null,
    assigned_clients_count: record.assigned_clients_count,
    assigned_prospects_count: record.assigned_prospects_count,
    assigned_deals_count: record.assigned_deals_count,
    team_members_count: record.team_members_count,
    created: record.created ?? '',
    updated: record.updated ?? '',
    expand: record.expand ? {
      lead_agent_id: record.expand.lead_agent_id
        ? mapTeamMember(record.expand.lead_agent_id)
        : undefined
    } : undefined,
  }
}

/**
 * Get all team members for an agency
 */
export async function getAgencyTeamMembers(agencyId: string): Promise<TeamMember[]> {
  const records = await pb.collection('users').getFullList<Record<string, any>>({
    filter: `agency_id = "${escapeFilterValue(agencyId)}"`,
    sort: 'first_name,last_name',
    perPage: 500,
  })

  return records.map(mapTeamMember)
}

/**
 * Get a single team member by ID
 */
export async function getTeamMember(userId: string): Promise<TeamMember> {
  const record = await pb.collection('users').getOne<Record<string, any>>(userId)

  return mapTeamMember(record)
}

/**
 * Assign an agent to a client, prospect, or deal
 */
export async function assignAgent(payload: AssignmentPayload): Promise<boolean> {
  const { entityType, entityId, agentId } = payload

  const collectionMap = {
    client: 'clients',
    prospect: 'prospects',
    deal: 'deals',
  }

  // Each collection uses a different primary owner field for My Work filtering
  const ownerFieldMap = {
    client: 'agent_owner_id',
    prospect: 'owner_id',
    deal: 'user_id',
  }

  const collection = collectionMap[entityType]
  if (!collection) {
    throw new Error(`Invalid entity type: ${entityType}`)
  }

  await pb.collection(collection).update(entityId, entityType === 'client' ? clientOwnerPatch(agentId) : {
    assigned_agent_id: agentId,
    [ownerFieldMap[entityType]]: agentId,
  })

  return true
}

/**
 * Get the assigned agent for an entity
 */
export async function getAssignedAgent(
  entityType: 'client' | 'prospect' | 'deal',
  entityId: string
): Promise<TeamMember | null> {
  const collectionMap = {
    client: 'clients',
    prospect: 'prospects',
    deal: 'deals',
  }

  const collection = collectionMap[entityType]
  const record = await pb.collection(collection).getOne<Record<string, any>>(entityId, {
    expand: 'assigned_agent_id',
  })

  if (!record.expand?.assigned_agent_id) {
    return null
  }

  return mapTeamMember(record.expand.assigned_agent_id)
}

/**
 * Build filter string for "My Work" view
 * Returns records where user is assigned or owner
 *
 * IMPORTANT: Each collection uses a different field for the assigned agent:
 * - clients: agent_owner_id
 * - deals: user_id
 * - prospects: owner_id
 */
export function getMyWorkFilter(userId: string, collection: 'clients' | 'deals' | 'prospects'): string {
  const safeUserId = escapeFilterValue(userId)

  // Collection-specific primary owner field
  const ownerField = collection === 'clients' ? 'agent_owner_id'
    : collection === 'deals' ? 'user_id'
    : 'owner_id'

  // assigned_agent_id is the other way an agent is attached to a record: the
  // assignment UI sets it without touching the primary owner field.
  const own = `${ownerField} = "${safeUserId}" || assigned_agent_id = "${safeUserId}"`

  // A deal belongs to an agent's book when it is on their athlete, whether or
  // not they created it (deals.client_id is a relation; see utils/athleteScope).
  if (collection === 'deals') {
    return `(${own} || client_id.agent_owner_id = "${safeUserId}" || client_id.assigned_agent_id = "${safeUserId}" || client_id.owner_id = "${safeUserId}")`
  }
  return `(${own})`
}

/**
 * Get work summary for a user
 */
export async function getWorkSummary(userId: string, agencyId: string): Promise<WorkSummary> {
  const safeUserId = escapeFilterValue(userId)
  const safeAgencyId = escapeFilterValue(agencyId)

  // Use collection-specific filters
  const clientsFilter = `${getMyWorkFilter(userId, 'clients')} && agency_id = "${safeAgencyId}"`
  const prospectsFilter = `${getMyWorkFilter(userId, 'prospects')} && agency_id = "${safeAgencyId}"`
  const dealsFilter = `${getMyWorkFilter(userId, 'deals')} && agency_id = "${safeAgencyId}"`

  // Count my direct assignments
  const [myClients, myProspects, myDeals, myTasks] = await Promise.all([
    pb.collection('clients').getList(1, 1, { filter: clientsFilter, fields: 'id' }).then(r => r.totalItems),
    pb.collection('prospects').getList(1, 1, { filter: prospectsFilter, fields: 'id' }).then(r => r.totalItems),
    pb.collection('deals').getList(1, 1, { filter: dealsFilter, fields: 'id' }).then(r => r.totalItems),
    pb.collection('tasks').getList(1, 1, {
      filter: `(assigned_to = "${safeUserId}" || created_by = "${safeUserId}") && agency_id = "${safeAgencyId}"`,
      fields: 'id'
    }).then(r => r.totalItems),
  ])

  const summary: WorkSummary = {
    userId,
    myClients,
    myProspects,
    myDeals,
    myTasks,
  }

  // Note: lead_agent_id functionality removed - using single owner field per collection
  // Team counts are now the same as personal counts since ownership is unified

  return summary
}

/**
 * Get assignment statistics for team members
 */
export async function getTeamAssignmentStats(
  agencyId: string
): Promise<Map<string, { clients: number; prospects: number; deals: number }>> {
  const members = await getAgencyTeamMembers(agencyId)
  const stats = new Map<string, { clients: number; prospects: number; deals: number }>()

  // Get all assigned entities (with pagination limit to prevent memory issues)
  // Use collection-specific owner fields: clients=agent_owner_id, deals=user_id, prospects=owner_id
  const [clients, prospects, deals] = await Promise.all([
    pb.collection('clients').getFullList<Record<string, any>>({
      filter: `agency_id = "${escapeFilterValue(agencyId)}"`,
      fields: 'id,agent_owner_id',
      perPage: 500,
    }),
    pb.collection('prospects').getFullList<Record<string, any>>({
      filter: `agency_id = "${escapeFilterValue(agencyId)}"`,
      fields: 'id,owner_id',
      perPage: 500,
    }),
    pb.collection('deals').getFullList<Record<string, any>>({
      filter: `agency_id = "${escapeFilterValue(agencyId)}"`,
      fields: 'id,user_id',
      perPage: 500,
    }),
  ])

  // Initialize stats for each member
  for (const member of members) {
    stats.set(member.id, { clients: 0, prospects: 0, deals: 0 })
  }

  // Count assignments using collection-specific owner fields
  for (const client of clients) {
    if (client.agent_owner_id && stats.has(client.agent_owner_id)) {
      stats.get(client.agent_owner_id)!.clients++
    }
  }
  for (const prospect of prospects) {
    if (prospect.owner_id && stats.has(prospect.owner_id)) {
      stats.get(prospect.owner_id)!.prospects++
    }
  }
  for (const deal of deals) {
    if (deal.user_id && stats.has(deal.user_id)) {
      stats.get(deal.user_id)!.deals++
    }
  }

  return stats
}

/**
 * Result from bulk assignment operation
 */
export interface BulkAssignResult {
  successCount: number
  failureCount: number
  failedIds: string[]
}

/**
 * Bulk assign agents to entities
 * Returns detailed result including which IDs failed
 *
 * Uses collection-specific owner fields:
 * - clients: agent_owner_id
 * - deals: user_id
 * - prospects: owner_id
 */
export async function bulkAssign(
  entityType: 'client' | 'prospect' | 'deal',
  entityIds: string[],
  agentId: string | null
): Promise<BulkAssignResult> {
  const collectionMap = {
    client: 'clients',
    prospect: 'prospects',
    deal: 'deals',
  }

  // Collection-specific owner field names
  const ownerFieldMap = {
    client: 'agent_owner_id',
    prospect: 'owner_id',
    deal: 'user_id',
  }

  const collection = collectionMap[entityType]
  const ownerField = ownerFieldMap[entityType]
  let successCount = 0
  const failedIds: string[] = []

  for (const entityId of entityIds) {
    try {
      await pb.collection(collection).update(entityId, entityType === 'client' ? clientOwnerPatch(agentId) : {
        [ownerField]: agentId,
      })
      successCount++
    } catch {
      failedIds.push(entityId)
    }
  }

  return {
    successCount,
    failureCount: failedIds.length,
    failedIds,
  }
}
